(function () {
  /**
   * Size and position of the pagination thumb, as fractions of the bar.
   * scrollable is false when every slide already fits.
   */
  function galleryThumb(scrollLeft, scrollWidth, clientWidth) {
    const overflow = scrollWidth - clientWidth;
    if (clientWidth <= 0 || overflow <= 1) {
      return { scrollable: false, size: 1, offset: 0, atStart: true, atEnd: true };
    }
    const size = clientWidth / scrollWidth;
    const progress = Math.min(Math.max(scrollLeft / overflow, 0), 1);
    return {
      scrollable: true,
      size,
      offset: progress * (1 - size),
      atStart: scrollLeft <= 1,
      atEnd: scrollLeft >= overflow - 1,
    };
  }

  /** scrollLeft for a pointer at `fraction` (0–1) of the bar, centring the thumb there. */
  function scrollForBarPosition(fraction, scrollWidth, clientWidth) {
    const overflow = Math.max(scrollWidth - clientWidth, 0);
    const size = scrollWidth > 0 ? clientWidth / scrollWidth : 1;
    if (size >= 1) return 0;
    const start = (fraction - size / 2) / (1 - size);
    return Math.min(Math.max(start, 0), 1) * overflow;
  }

  /** Nav entries from elements carrying data-pdp-nav-label, in page order. */
  function navEntries(elements) {
    const entries = [];
    for (const el of elements) {
      const label = (el.dataset.pdpNavLabel || '').trim();
      if (label && el.id) entries.push({ id: el.id, label });
    }
    return entries;
  }

  /** Visible height of the site header still occupying the top of the viewport. */
  function headerViewportOffset(headerBottom) {
    return Math.max(0, headerBottom);
  }

  /** True once the hero's bottom edge has scrolled up under the site header. */
  function stickyBarVisible(heroBottom, headerHeight) {
    return heroBottom <= headerHeight;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      galleryThumb,
      scrollForBarPosition,
      navEntries,
      stickyBarVisible,
      headerViewportOffset,
    };
  }

  if (typeof customElements === 'undefined') return;

  class PdpGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pdp-track]');
      this.controls = this.querySelector('[data-pdp-controls]');
      this.bar = this.querySelector('[data-pdp-bar]');
      this.prev = this.querySelector('[data-pdp-prev]');
      this.next = this.querySelector('[data-pdp-next]');
      if (!this.track) return;

      this.update = this.update.bind(this);
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);

      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));
      this.bar?.addEventListener('pointerdown', (event) => this.startDrag(event));

      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.track?.removeEventListener('scroll', this.onScroll);
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const thumb = galleryThumb(scrollLeft, scrollWidth, clientWidth);
      this.classList.toggle('is-scrollable', thumb.scrollable);
      if (this.controls) this.controls.hidden = !thumb.scrollable;
      if (!thumb.scrollable) return;

      this.style.setProperty('--thumb-size', `${thumb.size * 100}%`);
      // translate() percentages are relative to the thumb, so convert bar fraction → thumb widths.
      this.style.setProperty('--thumb-offset', `${(thumb.offset / thumb.size) * 100}%`);
      if (this.prev) this.prev.disabled = thumb.atStart;
      if (this.next) this.next.disabled = thumb.atEnd;
    }

    step(direction) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({
        left: direction * this.track.clientWidth * 0.8,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    startDrag(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      this.bar.setPointerCapture(event.pointerId);
      const move = (e) => {
        const rect = this.bar.getBoundingClientRect();
        const fraction = (e.clientX - rect.left) / rect.width;
        this.track.scrollLeft = scrollForBarPosition(fraction, this.track.scrollWidth, this.track.clientWidth);
      };
      const end = () => {
        this.bar.removeEventListener('pointermove', move);
        this.bar.removeEventListener('pointerup', end);
        this.bar.removeEventListener('pointercancel', end);
      };
      this.bar.addEventListener('pointermove', move);
      this.bar.addEventListener('pointerup', end);
      this.bar.addEventListener('pointercancel', end);
      move(event);
    }
  }

  class PdpNav extends HTMLElement {
    connectedCallback() {
      this.render();
      // Theme editor: unload fires before the section leaves the DOM, so re-read on the next tick.
      this.onSectionChange = () => setTimeout(() => this.render());
      document.addEventListener('shopify:section:load', this.onSectionChange);
      document.addEventListener('shopify:section:unload', this.onSectionChange);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:load', this.onSectionChange);
      document.removeEventListener('shopify:section:unload', this.onSectionChange);
    }

    render() {
      const list = this.querySelector('ul');
      if (!list) return;
      const entries = navEntries(document.querySelectorAll('[data-pdp-nav-label]'));
      list.replaceChildren(
        ...entries.map(({ id, label }) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = `#${id}`;
          link.textContent = label;
          item.append(link);
          return item;
        })
      );
      this.hidden = entries.length === 0;
    }
  }

  class PdpStickyBar extends HTMLElement {
    connectedCallback() {
      this.hero = document.getElementById(this.dataset.hero);
      this.price = this.querySelector('[data-pdp-sticky-price]');
      this.addButton = this.querySelector('[data-pdp-sticky-add]');
      this.titleEl = this.querySelector('[data-pdp-sticky-title]');
      this.heroPrice = document.getElementById(`price-${this.dataset.sectionId}`);
      this.heroAdd = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateVisibility();
        });
      };
      this.onAdd = () => {
        if (!this.heroAdd || this.addButton?.disabled) return;
        if (this.addButton?.getAttribute('aria-disabled') === 'true') return;
        this.heroAdd.click();
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.addButton?.addEventListener('click', this.onAdd);

      this.syncFromHero();
      this.heroObserver = new MutationObserver(() => this.syncFromHero());
      if (this.heroPrice) {
        this.heroObserver.observe(this.heroPrice, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
      }
      if (this.heroAdd) {
        this.heroObserver.observe(this.heroAdd, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'disabled', 'aria-disabled'],
        });
      }

      this.updateVisibility();
      this.updateTitleOverflow();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.addButton?.removeEventListener('click', this.onAdd);
      this.heroObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    headerHeightPx() {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
      if (raw.endsWith('px')) return parseFloat(raw);
      const header = document.querySelector('sticky-header header');
      if (header) return header.getBoundingClientRect().height;
      return 50;
    }

    headerOffsetPx() {
      const header =
        document.querySelector('sticky-header header') || document.querySelector('.section-header');
      if (header) return headerViewportOffset(header.getBoundingClientRect().bottom);
      return this.headerHeightPx();
    }

    applyHeaderOffset() {
      const offset = this.headerOffsetPx();
      const value = `${offset}px`;
      this.style.setProperty('--pdp-header-offset', value);
      this.closest('.pdp')?.style.setProperty('--pdp-header-offset', value);
      return offset;
    }

    updateVisibility() {
      if (!this.hero) this.hero = document.getElementById(this.dataset.hero);
      if (!this.hero) return;
      const offset = this.applyHeaderOffset();
      const visible = stickyBarVisible(this.hero.getBoundingClientRect().bottom, offset);
      this.classList.toggle('is-visible', visible);
      this.toggleAttribute('inert', !visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) this.updateTitleOverflow();
    }

    updateTitleOverflow() {
      if (!this.titleEl) return;
      const truncated = this.titleEl.scrollWidth - this.titleEl.clientWidth > 1;
      this.titleEl.classList.toggle('is-truncated', truncated);
    }

    syncFromHero() {
      if (this.heroPrice && this.price) {
        this.price.innerHTML = this.heroPrice.innerHTML;
        this.price.classList.toggle('hidden', this.heroPrice.classList.contains('hidden'));
      }
      if (!this.heroAdd || !this.addButton) return;
      this.addButton.disabled = this.heroAdd.disabled;
      if (this.heroAdd.getAttribute('aria-disabled') === 'true' || this.heroAdd.disabled) {
        this.addButton.setAttribute('aria-disabled', 'true');
      } else {
        this.addButton.removeAttribute('aria-disabled');
      }
      const srcText = this.heroAdd.querySelector('span');
      const destText = this.addButton.querySelector('span');
      if (srcText && destText) destText.textContent = srcText.textContent;
      const loading = this.heroAdd.classList.contains('loading');
      this.addButton.classList.toggle('loading', loading);
      const spinner = this.addButton.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.toggle('hidden', !loading);
    }
  }

  if (!customElements.get('pdp-gallery')) customElements.define('pdp-gallery', PdpGallery);
  if (!customElements.get('pdp-nav')) customElements.define('pdp-nav', PdpNav);
  if (!customElements.get('pdp-sticky-bar')) customElements.define('pdp-sticky-bar', PdpStickyBar);
})();
